<template>
  <div class="kiosk-products-area">
    <div class="products-header">
      <div class="products-header-top">
        <div class="products-header-left">
          <h2 class="products-title">
            {{ title }}
          </h2>
        </div>
        <div class="products-header-right">
          <button
            class="cancel-btn btn-reset"
            @click="$emit('cancel-order')"
            type="button"
          >
            Cancel Order
          </button>
        </div>
      </div>

      <div
        v-if="diningOptions && diningOptions.length > 0"
        class="dining-options-toggle"
      >
        <button
          v-for="option in diningOptions"
          :key="option.id"
          @click="$emit('select-dining-option', option.id)"
          class="dining-option-btn"
          :class="{ active: currentDiningOption === option.id }"
        >
          <KioskIcon
            :name="getDiningIcon(option.name)"
            class="dining-option-icon"
            size="14"
            stroke-width="2"
          />
          <span class="dining-option-text">{{ option.name }}</span>
        </button>
      </div>
    </div>

    <div class="products-grid">
      <div
        v-for="product in products"
        :key="product.id"
        class="product-card"
        @click="$emit('open-builder', product)"
      >
        <div class="product-image-wrapper">
          <img
            v-if="product.image"
            :src="product.image"
            :alt="product.name"
            class="product-image"
          >
          <div
            v-else
            class="product-placeholder"
          >
            <KioskIcon
              name="placeholder"
              size="48"
              stroke-width="1.5"
            />
          </div>
          <button
            v-if="hasDescription(product)"
            class="product-info-btn"
            @click.stop.prevent="$emit('show-info', product)"
          >
            <KioskIcon
              name="info"
              size="16"
              stroke-width="2"
            />
          </button>
        </div>
        <div class="product-info">
          <h3 class="product-name">
            {{ product.name }}
          </h3>
          <div class="product-footer">
            <span
              class="product-price"
            >{{ currency }} {{ (product.price || 0).toFixed(2) }}</span>
            <button
              v-if="
                !product.modifier_groups || product.modifier_groups.length === 0
              "
              class="product-add-btn btn-reset"
              :class="{
                adding: addingId === product.id,
                added: addedId === product.id,
              }"
              @click.stop="$emit('add-directly', product)"
            >
              <span class="btn-label">Add to Order</span>
            </button>
            <button
              v-else
              class="product-add-btn btn-reset"
              @click.stop="$emit('open-builder', product)"
            >
              <span>Customize</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import KioskIcon from "./KioskIcon.vue";

export default {
  name: "ProductsGridV2",
  components: {
    KioskIcon,
  },
  props: {
    products: Array,
    title: String,
    diningOptions: Array,
    currentDiningOption: [String, Number],
    currency: String,
    addingId: [String, Number],
    addedId: [String, Number],
    hasDescription: Function,
  },
  methods: {
    getDiningIcon(name) {
      const lower = name.toLowerCase();
      if (lower.includes("dine") || lower.includes("mesa")) return "dine";
      if (
        lower.includes("go") ||
        lower.includes("take") ||
        lower.includes("llevar")
      )
        return "take";
      return "generic";
    },
  },
};
</script>

<style scoped>
.kiosk-products-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f9fafb;
}

.products-header {
  background: white;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
}

.products-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.products-header-left {
  display: flex;
  align-items: center;
}

.products-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.products-title {
  font-family: "Merriweather", serif;
  font-weight: bold;
  font-size: 20px;
  color: #111827;
  margin: 0;
}

.btn-reset {
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
  margin: 0;
}

.cancel-btn {
  color: #ef4444;
  background: #fef2f2;
  font-weight: 700;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  white-space: nowrap;
  transition: all 0.2s;
}

.cancel-btn:hover {
  color: #dc2626;
}

.dining-options-toggle {
  display: flex;
  background: #f3f4f6;
  padding: 4px;
  border-radius: 12px;
  gap: 4px;
  flex-shrink: 0;
  width: fit-content;
}

.dining-option-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.dining-option-btn:hover {
  color: #4b5563;
}

.dining-option-btn.active {
  background: white;
  color: #2563eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.dining-option-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.dining-option-text {
  font-size: 12px;
  font-weight: bold;
}

.products-grid {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: 360px;
  gap: 16px;
  padding-bottom: 120px;
  align-items: stretch;
  align-content: start;
}

.product-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s;
  display: grid;
  grid-template-rows: 1fr 6rem;
  height: 100%;
  min-height: 340px;
}

.product-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transform: scale(0.98);
}

.product-image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  aspect-ratio: 4/3;
  background: #f3f4f6;
  overflow: hidden;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
}

.product-card:hover .product-image {
  transform: scale(1.1);
}

.product-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  color: #9ca3af;
}

.product-info-btn {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: #6b7280;
}

.product-info-btn:hover {
  background: white;
  transform: scale(1.1);
}

.product-info {
  padding: 12px;
  gap: 8px;
  display: flex;
  flex-direction: column;
  min-height: 6rem;
  justify-content: space-between;
}

.product-name {
  text-align: start;
  font-weight: bold;
  font-size: 15px;
  color: #111827;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.product-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid #f3f4f6;
  flex-shrink: 0;
  min-height: 32px;
}

.product-price {
  font-weight: bold;
  font-size: 16px;
  color: #0f172a;
  flex-shrink: 0;
  white-space: nowrap;
  margin-right: 8px;
}

.product-add-btn {
  padding: 8px 16px;
  background: #2563eb;
  color: white;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  position: relative;
  overflow: hidden;
}

.product-add-btn.adding {
  background: #3b82f6;
  pointer-events: none;
}

.product-add-btn.added {
  background: #10b981;
  color: white;
  transform: scale(1.05);
}

.product-add-btn .btn-label {
  display: flex;
  align-items: center;
  gap: 4px;
}

.product-add-btn.added::before {
  content: "✓";
  margin-right: 4px;
  font-size: 14px;
}

.product-card:hover .product-add-btn {
  background: #1d4ed8;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
}

.product-card:hover .product-add-btn.added {
  background: #10b981;
}
</style>
